package com.wintermar.inspection.core.ui.component.image

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.runtime.Composable
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import com.wintermar.inspection.core.ui.component.TextBodySmall
import com.wintermar.inspection.core.ui.component.TextBodyTiny
import com.wintermar.inspection.core.ui.component.button.IconButton
import com.wintermar.inspection.core.ui.component.image.util.FileDownloader
import com.wintermar.inspection.core.ui.component.image.util.FileMimeType
import com.wintermar.inspection.core.ui.component.image.util.FileModel
import com.wintermar.inspection.core.ui.component.image.util.FileUtil
import com.wintermar.inspection.core.ui.theme.AppDimension
import com.wintermar.inspection.core.ui.theme.LocalAppTheme
import com.wintermar.inspection.core.ui.theme.color.model.ThemeColor
import com.wintermar.inspection.core.ui.widget.HorizontalProgress
import inspection.composeapp.generated.resources.Res
import inspection.composeapp.generated.resources.ic_close
import kotlinx.coroutines.launch

@Composable
fun Document(
    file: FileModel,
    showDeleteButton: Boolean = false,
    onDelete: () -> Unit = {}
) {
    val dimension: AppDimension = LocalAppTheme.current.dimension
    val color: ThemeColor = LocalAppTheme.current.color
    val scope = rememberCoroutineScope()

    fun handleOnClick() {
        scope.launch {
            FileDownloader.download(
                file,
                FileMimeType.getMimeTypeFromExtension(file.name.substringAfterLast("."))
            )
        }
    }

    Box(
        modifier = Modifier.fillMaxWidth(),
        contentAlignment = Alignment.TopEnd
    ) {
        Row(
            modifier = Modifier.clip(
                shape = RoundedCornerShape(dimension.radius.md)
            )
                .background(
                    color.surface.normal
                )
                .border(
                    width = 1.dp,
                    color = color.border.subtle,
                    shape = RoundedCornerShape(dimension.radius.md)
                )

                .clickable(enabled = !file.isUploading(), onClick = ::handleOnClick)
                .padding(
                    vertical = dimension.spacing.space2,
                    horizontal = dimension.spacing.space3
                ),
            verticalAlignment = if (showDeleteButton) Alignment.Top else Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(dimension.spacing.space2)
        ) {
            Column(
                modifier = Modifier.weight(1f)
            ) {
                TextBodySmall(
                    text = file.originalName,
                    maxLines = 1,
                    modifier = Modifier.fillMaxWidth()
                )

                if (showDeleteButton && file.acceptableToDelete()) {
                    TextBodyTiny(
                        text = FileUtil.getDisplaySizeInKb(file.size.toInt()),
                        color = { it.subtle }
                    )
                }
            }

            if (showDeleteButton && file.acceptableToDelete()) {
                IconButton(
                    icon = Res.drawable.ic_close,
                    tint = color.icon.subtle,
                    backgroundColor = Color.Transparent,
                    contentDescription = "delete",
                    modifier = Modifier.size(dimension.size.icon.s),
                    onClick = { onDelete() }
                )
            } else {
                TextBodyTiny(
                    text = FileUtil.getDisplaySizeInKb(file.size.toInt()),
                    color = { it.subtle }
                )
            }
        }
        AnimatedVisibility(
            visible = file.isUploading(),
            enter = fadeIn(),
            exit = fadeOut()
        ) {
            Row(
                modifier = Modifier
                    .fillMaxSize()
                    .clip(
                        shape = RoundedCornerShape(dimension.radius.md)
                    )
                    .background(
                        color = Color.Black.copy(alpha = 0.75f)
                    )
            ) {
                HorizontalProgress(
                    progress = file.currentUpload?.chunkNumber?.plus(1) ?: 0,
                    total = file.currentUpload?.totalFiles ?: 0,
                    modifier = Modifier.weight(1f)
                )
            }
        }
    }
}
